import { tagsInputAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const extendedTagsInputAnatomy = tagsInputAnatomy.extendWith('tagsBlock', 'suffixBlock');

const tagsInputSlotRecipe = defineSlotRecipe({
  className: 'tags-input',
  slots: extendedTagsInputAnatomy.keys(),
  base: {
    root: {
      width: 'full',
    },
    control: {
      alignItems: 'flex-start',
      background: 'background/primary',
      borderColor: 'border/regular',
      borderRadius: '4',
      borderStyle: 'solid',
      borderWidth: '1',
      boxShadow: 'inset/field',
      display: 'flex',
      height: rem(88),
      overflowY: 'auto',
      transition: 'border-color 200ms',
      _focusWithin: {
        focusRing: 'outside',
      },
      _hover: {
        borderColor: 'border/hover',
      },
      _invalid: {
        borderColor: 'border/error',
        _hover: {
          borderColor: 'border/error',
        },
      },
      _disabled: {
        background: 'background/disabled',
        borderColor: 'border/disabled',
        cursor: 'not-allowed',
        _hover: {
          borderColor: 'border/disabled',
        },
      },
      _readOnly: {
        background: 'background/disabled',
        borderColor: 'border/disabled',
        _hover: {
          borderColor: 'border/disabled',
        },
      },
    },
    tagsBlock: {
      alignContent: 'flex-start',
      alignItems: 'center',
      display: 'flex',
      flex: '1',
      flexWrap: 'wrap',
      gap: '8',
      minWidth: 0,
    },
    suffixBlock: {
      alignItems: 'center',
      alignSelf: 'flex-start',
      display: 'flex',
      flexShrink: '0',
      gap: '8',
      paddingInline: '4',
      position: 'sticky',
      top: 0,
      '&:has(button)': {
        paddingInline: 0,
      },
    },
    input: {
      background: 'transparent',
      color: 'input/text/inputValue',
      flex: '1',
      lineHeight: 'normal',
      minWidth: rem(80),
      outline: 'none',
      // Per Figma, the empty-state placeholder is offset 4px further from the control's
      // left edge than a filled-state tag chip. That 4px comes from an "input" layer in
      // Figma that wraps the typing area — in our recipe we apply it as a left padding on
      // the typing <input> itself so tag chips (which live alongside but not inside the
      // input) remain flush with the control's padding.
      paddingInlineStart: '4',
      _placeholder: {
        color: 'input/text/placeholder',
      },
      _disabled: {
        cursor: 'not-allowed',
        _placeholder: {
          color: 'text/disabled',
        },
      },
      _readOnly: {
        display: 'none',
      },
    },
    item: {
      display: 'flex',
      maxWidth: '100%',
      minWidth: 0,
    },
    itemPreview: {
      alignItems: 'center',
      background: 'color/neutral/subtle',
      borderColor: 'color/neutral/muted',
      borderRadius: '4',
      borderStyle: 'solid',
      borderWidth: '1',
      color: 'text/primary',
      display: 'flex',
      gap: '4',
      height: '24',
      maxWidth: '100%',
      overflow: 'hidden',
      paddingBlock: '2',
      paddingInlineEnd: '1',
      paddingInlineStart: '8',
      textStyle: 'comp/tag/sm',
      _highlighted: {
        background: 'color/neutral/moderate',
      },
      _disabled: {
        background: 'background/disabled',
        borderColor: 'border/disabled',
        color: 'text/on-disabled',
      },
      '&:not(:has(button))': {
        paddingInlineEnd: '8',
      },
      '&[data-invalid]': {
        background: 'color/red/subtle',
        borderColor: 'color/red/muted',
        color: 'color/red/strong',
        _highlighted: {
          background: 'color/red/moderate',
        },
        '& [data-part="item-delete-trigger"]': {
          color: 'color/red/strong',
          _hover: {
            background: 'color/red/moderate',
          },
        },
      },
    },
    itemText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    itemInput: {
      background: 'transparent',
      height: '24',
      minWidth: '20',
      outline: 'none',
    },
    itemDeleteTrigger: {
      alignItems: 'center',
      borderRadius: '2',
      color: 'icon/secondary',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: '0',
      height: '20',
      justifyContent: 'center',
      width: '20',
      _hover: {
        background: 'color/neutral/moderate',
      },
      _disabled: {
        cursor: 'not-allowed',
        opacity: '0.5',
      },
    },
    clearTrigger: {
      alignItems: 'center',
      borderRadius: '4',
      color: 'icon/secondary',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      padding: '4',
      _hover: {
        background: 'color/neutral/moderate',
      },
      _disabled: {
        cursor: 'not-allowed',
        opacity: '0.5',
      },
    },
  },
  variants: {
    size: {
      // Padding values below match the Figma control + input layer spec exactly
      // (see FRONTEND-572 thread / Figma node 1941:3510 for md, 1941:3487 for lg).
      // The outer control carries the size-specific padding; the inner tagsBlock adds a
      // constant 4px horizontal padding (set in the base) around the content regardless of size.
      md: {
        control: {
          paddingInlineEnd: '12',
          paddingInlineStart: '8',
        },
        // paddingBlock lives on tagsBlock (not control) so the suffixBlock (clear button) sits
        // at the control's inner top and its vertical center lines up with the first tag-row /
        // placeholder center — matches Figma's filled-state layout.
        tagsBlock: {
          paddingBlock: '8',
        },
        suffixBlock: {
          height: '40',
        },
        input: {
          height: '24',
          fontSize: '0.875rem',
          fontWeight: '400',
          lineHeight: 'normal',
        },
      },
      lg: {
        control: {
          paddingInlineEnd: '16',
          paddingInlineStart: '12',
        },
        tagsBlock: {
          paddingBlock: '12',
        },
        suffixBlock: {
          height: '48',
        },
        input: {
          fontSize: '1rem',
          fontWeight: '400',
          lineHeight: 'normal',
        },
      },
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

export default tagsInputSlotRecipe;
